import React, { useCallback, useEffect, useState } from 'react';
import { Archive, Download, FileSpreadsheet, RefreshCw, Search } from 'lucide-react';
import { apiCall } from './_shared';
import { downloadPdf } from './GuestMemoryCard';

const money = (v) => `€${Number(v || 0).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const ts = (s) => (s ? s.slice(0, 16).replace('T', ' ') : '—');
const STATUS_STYLE = { confirmed: 'bg-emerald-50 text-emerald-800 border-emerald-200', new: 'bg-sky-50 text-sky-800 border-sky-200', request: 'bg-amber-50 text-amber-800 border-amber-200', cancelled: 'bg-rose-50 text-rose-700 border-rose-200', black: 'bg-slate-100 text-slate-700 border-slate-200' };

const YearTable = ({ perYear }) => {
  const years = perYear || [];
  const rows = years.map((y) => (
    <tr key={y.year} className="border-t" data-testid={`archive-year-${y.year}`}>
      <td className="py-1 pr-3 font-semibold text-slate-900">{y.year}</td>
      <td className="py-1 pr-3 text-right">{y.bookings}</td>
      <td className="py-1 pr-3 text-right text-emerald-700">{y.active}</td>
      <td className="py-1 pr-3 text-right text-rose-600">{y.cancelled}</td>
      <td className="py-1 pr-3 text-right">{y.nights}</td>
      <td className="py-1 text-right font-semibold">{money(y.revenue)}</td>
    </tr>
  ));
  return (
    <table className="text-xs w-full max-w-xl" data-testid="archive-year-table">
      <thead><tr className="text-slate-500 text-left"><th className="pb-1">Arrival year</th><th className="text-right pr-3">Bookings</th><th className="text-right pr-3">Active</th><th className="text-right pr-3">Cancelled</th><th className="text-right pr-3">Nights</th><th className="text-right">Revenue</th></tr></thead>
      <tbody>{rows}</tbody>
    </table>
  );
};

const ArchiveRows = ({ rows }) => {
  const items = rows || [];
  const list = items.map((b) => {
    const st = (b.status || '').toLowerCase();
    return (
      <tr key={b.beds24_id} className="border-t hover:bg-slate-50" data-testid={`archive-row-${b.beds24_id}`}>
        <td className="py-1.5 pr-3 text-slate-500">{b.beds24_id}</td>
        <td className="py-1.5 pr-3"><span className="font-semibold text-slate-900">{b.guest_name}</span>{b.country ? <span className="text-slate-500"> · {b.country}</span> : null}<br /><span className="text-[11px] text-slate-500">{b.email || b.phone || '—'}</span></td>
        <td className="py-1.5 pr-3 whitespace-nowrap">{b.check_in} → {b.check_out}<br /><span className="text-[11px] text-slate-500">{b.nights} night{b.nights === 1 ? '' : 's'} · {b.adults}+{b.children}</span></td>
        <td className="py-1.5 pr-3">{b.room_name}</td>
        <td className="py-1.5 pr-3 text-slate-600">{b.source}</td>
        <td className="py-1.5 pr-3 text-right font-semibold">{money(b.total_price)}</td>
        <td className="py-1.5"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[st] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>{b.status}</span></td>
      </tr>
    );
  });
  return <tbody>{list}</tbody>;
};

const Beds24ArchiveCard = ({ userRole }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [year, setYear] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [list, setList] = useState(null);
  const canRun = ['super_admin', 'admin', 'manager'].includes(userRole);

  const load = useCallback(async () => { try { setData(await apiCall('/api/admin/beds24/import-bookings')); } catch (e) { setMsg(String(e.message || e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const running = data?.state?.running;
  useEffect(() => { if (!running) return undefined; const id = setInterval(load, 3000); return () => clearInterval(id); }, [running, load]);

  const search = useCallback(async () => {
    const p = new URLSearchParams({ page: String(page), limit: '50' });
    if (q.trim()) p.set('q', q.trim());
    if (year) p.set('year', year);
    if (status !== 'all') p.set('status', status);
    try { setList(await apiCall(`/api/admin/beds24/bookings?${p.toString()}`)); } catch (e) { setMsg(String(e.message || e)); }
  }, [q, year, status, page]);
  useEffect(() => { if (open) search(); }, [open, search]);

  const startImport = async () => {
    if (!window.confirm('Import every booking from Beds24 (first to last)? Takes a few minutes and uses Beds24 API credits.')) return;
    setBusy(true); setMsg('');
    try { const r = await apiCall('/api/admin/beds24/import-bookings', { method: 'POST' }); setMsg(`Import started: ${r.from} → ${r.to} in ${r.windows} steps.`); await load(); } catch (e) { setMsg(String(e.message || e)); }
    setBusy(false);
  };
  const syncNow = async () => {
    setBusy(true); setMsg('');
    try { const r = await apiCall('/api/admin/beds24/import-bookings/sync?days=7', { method: 'POST' }); setMsg(r.error || r.skipped || `Synced ${r.upserted} booking(s) changed since ${ts(r.modified_since)}.`); await load(); } catch (e) { setMsg(String(e.message || e)); }
    setBusy(false);
  };

  const s = data?.summary; const st = data?.state;
  const pct = st?.windows_total ? Math.round(((st.windows_done || 0) / st.windows_total) * 100) : 0;
  const yearOptions = (s?.per_year || []).map((y) => <option key={y.year} value={y.year}>{y.year}</option>);
  const [xlsYear, setXlsYear] = useState('');
  const exportXlsx = () => downloadPdf(`/api/admin/beds24/bookings/export.xlsx${xlsYear ? `?year=${xlsYear}` : ''}`, `beds24_bookings_${xlsYear || 'all'}.xlsx`);
  const pageBtns = list && list.pages > 1 && (
    <div className="flex items-center gap-2 text-xs" data-testid="archive-pager">
      <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border rounded px-2 py-1 disabled:opacity-40" data-testid="archive-prev">‹</button>
      <span>page {list.page} / {list.pages}</span>
      <button type="button" disabled={page >= list.pages} onClick={() => setPage((p) => p + 1)} className="border rounded px-2 py-1 disabled:opacity-40" data-testid="archive-next">›</button>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="beds24-archive-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Archive className="w-4 h-4 text-orange-600" /> Beds24 booking archive — every booking, first to last</h3>
          <p className="text-xs text-slate-500 mt-1">A full copy of all Beds24 bookings kept in our own database (names, dates, room, channel, price, invoice lines), refreshed every night at 05:45 with whatever changed in Beds24. The live list below still only shows a date window — this archive has no limit.</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-slate-900" data-testid="archive-total">{s?.total ?? '—'}</p>
          <p className="text-[11px] text-slate-500">bookings archived{s?.first_arrival ? ` · ${s.first_arrival} → ${s.last_arrival}` : ''}</p>
        </div>
      </div>

      {st?.status && st.status !== 'never' && (
        <div className="text-xs rounded-lg border bg-slate-50 p-3 space-y-1" data-testid="archive-state">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span>
              {running ? <b className="text-orange-700">Importing… {st.current_window || ''}</b> : <b className={st.status === 'done' ? 'text-emerald-700' : 'text-amber-700'}>{st.status === 'done' ? 'Last full import complete' : `Last import: ${st.status}`}</b>}
              {' '}· {st.from} → {st.to} · fetched {st.fetched ?? 0} · saved {st.upserted ?? 0}{st.finished_at ? ` · finished ${ts(st.finished_at)}` : ''}{st.by ? ` · by ${st.by}` : ''}
            </span>
            {st.last_sync?.at && <span className="text-slate-500">Nightly sync {ts(st.last_sync.at)}: {st.last_sync.error || `${st.last_sync.upserted} updated`}</span>}
          </div>
          {(running || st.windows_total) && <div className="h-1.5 bg-slate-200 rounded overflow-hidden"><div className="h-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} /></div>}
          {st.last_error && <p className="text-amber-700" data-testid="archive-error">{st.last_error}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {canRun && <button type="button" onClick={startImport} disabled={busy || running} className="inline-flex items-center gap-1 text-sm bg-slate-900 text-white rounded-lg px-3 py-1.5 disabled:opacity-50" data-testid="archive-import-all"><Download className="w-3.5 h-3.5" /> Import all bookings now</button>}
        {canRun && <button type="button" onClick={syncNow} disabled={busy || running} className="inline-flex items-center gap-1 text-sm border rounded-lg px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50" data-testid="archive-sync"><RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Sync last 7 days</button>}
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm border rounded-lg px-3 py-1.5 hover:bg-slate-50" data-testid="archive-browse"><Search className="w-3.5 h-3.5" /> {open ? 'Hide archive' : 'Browse / search archive'}</button>
        <span className="inline-flex items-center gap-1 ml-auto" data-testid="archive-excel">
          <select value={xlsYear} onChange={(e) => setXlsYear(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="archive-excel-year"><option value="">Whole archive</option>{yearOptions}</select>
          <button type="button" onClick={exportXlsx} disabled={!s?.total} className="inline-flex items-center gap-1 text-sm bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-50" data-testid="archive-excel-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> Excel</button>
        </span>
        {msg && <span className="text-xs text-slate-700" data-testid="archive-msg">{msg}</span>}
      </div>

      {s?.per_year?.length > 0 && <YearTable perYear={s.per_year} />}

      {open && (
        <div className="space-y-3" data-testid="archive-browser">
          <div className="flex flex-wrap gap-2 items-center">
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Guest, e-mail, phone, Beds24 id, room, country…" className="border rounded-lg px-3 py-1.5 text-sm min-w-[260px]" data-testid="archive-q" />
            <select value={year} onChange={(e) => { setYear(e.target.value); setPage(1); }} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="archive-year"><option value="">All years</option>{yearOptions}</select>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="archive-status">
              <option value="all">All statuses</option><option value="confirmed">Confirmed</option><option value="new">New</option><option value="request">Request</option><option value="cancelled">Cancelled</option><option value="black">Black</option>
            </select>
            <span className="text-xs text-slate-500" data-testid="archive-count">{list ? `${list.total} match` : ''}</span>
            {pageBtns}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="archive-table">
              <thead><tr className="text-left text-slate-500"><th className="pb-1 pr-3">Beds24 #</th><th className="pr-3">Guest</th><th className="pr-3">Stay</th><th className="pr-3">Room</th><th className="pr-3">Channel</th><th className="text-right pr-3">Total</th><th>Status</th></tr></thead>
              {list && <ArchiveRows rows={list.rows || []} />}
            </table>
            {list && !list.rows?.length && <p className="text-xs text-slate-500 py-3" data-testid="archive-empty">Nothing in the archive matches — run the import first.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Beds24ArchiveCard;
